import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

export interface ErrorStateProps {
  readonly title?: string;
  readonly description?: string;
  /** Shown behind a disclosure — useful to support, noise to everyone else. */
  readonly details?: string;
  readonly onRetry?: () => void;
}

/**
 * The failure counterpart to EmptyState. Technical detail is present but folded away: support
 * needs it, and a user staring at a stack trace learns nothing except that we shipped one.
 */
export function ErrorState({ title, description, details, onRetry }: ErrorStateProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Alert
      severity="error"
      action={
        onRetry === undefined ? undefined : (
          <Button color="inherit" onClick={onRetry}>
            {t('actions.retry')}
          </Button>
        )
      }
    >
      <AlertTitle>{title ?? t('error.title')}</AlertTitle>
      <Stack spacing={1}>
        <Typography variant="body2">{description ?? t('error.description')}</Typography>
        {details !== undefined && (
          <Box>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => {
                setShowDetails((value) => !value);
              }}
              aria-expanded={showDetails}
            >
              {t('error.details')}
            </Button>
            <Collapse in={showDetails}>
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {details}
              </Typography>
            </Collapse>
          </Box>
        )}
      </Stack>
    </Alert>
  );
}
